import * as types from '../types/productTypes';
import { isApiEnabled } from '../../config/env';
import { modamaiTrace } from '../../utils/modamaiTrace';
import { productService } from '../../services/productService';
import {
  mapApiProductsToModamaiCatalog as mapProductsListToCatalog,
  mapApiProductToModamaiDetail,
} from '../../utils/mapApiProduct';

export const clearProductDetail = () => ({ type: types.CLEAR_PRODUCT_DETAIL });

/**
 * @param {Record<string, unknown>} [params]
 */
export const fetchShopCatalog = (params = {}) => async (dispatch) => {
  if (!isApiEnabled()) {
    modamaiTrace('fetchShopCatalog skipped (API off)', {});
    return;
  }
  modamaiTrace('fetchShopCatalog START', { params });
  dispatch({ type: types.FETCH_SHOP_CATALOG_REQUEST });
  try {
    const { products, pagination } = await productService.getProducts(params);
    const items = mapProductsListToCatalog(Array.isArray(products) ? products : []);
    modamaiTrace('fetchShopCatalog service OK → mapping', {
      rawCount: products?.length ?? 0,
      mappedCount: items?.length ?? 0,
      pagination,
    });
    dispatch({
      type: types.FETCH_SHOP_CATALOG_SUCCESS,
      payload: {
        items,
        pagination,
      },
    });
    modamaiTrace('fetchShopCatalog SUCCESS dispatched', items?.length ?? 0);
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || 'Failed to load products';
    modamaiTrace('fetchShopCatalog FAILURE', { msg, status: e?.response?.status });
    dispatch({ type: types.FETCH_SHOP_CATALOG_FAILURE, payload: msg });
  }
};

export const fetchProductDetail = (slug) => async (dispatch) => {
  if (!isApiEnabled()) {
    modamaiTrace('fetchProductDetail skipped (API off)', { slug });
    return;
  }
  if (!slug) {
    modamaiTrace('fetchProductDetail no slug', {});
    dispatch({
      type: types.FETCH_PRODUCT_DETAIL_FAILURE,
      payload: 'Missing slug',
    });
    return;
  }
  modamaiTrace('fetchProductDetail START', { slug });
  dispatch({ type: types.FETCH_PRODUCT_DETAIL_REQUEST });
  try {
    const raw = await productService.getProductBySlugOrId(slug);
    modamaiTrace('fetchProductDetail raw from service', {
      slug,
      hasRaw: Boolean(raw),
      rawKeys: raw && typeof raw === 'object' ? Object.keys(raw).slice(0, 15) : null,
    });
    const item = mapApiProductToModamaiDetail(raw);
    modamaiTrace('fetchProductDetail after map', {
      mappedSlug: item?.slug,
      hasItem: Boolean(item),
    });
    if (!item || !item.slug) {
      dispatch({ type: types.FETCH_PRODUCT_DETAIL_FAILURE, payload: 'Product not found' });
      return;
    }
    dispatch({ type: types.FETCH_PRODUCT_DETAIL_SUCCESS, payload: item });
    modamaiTrace('fetchProductDetail SUCCESS dispatched', { slug: item.slug });
  } catch (e) {
    const msg = e?.response?.data?.message || e?.message || 'Failed to load product';
    modamaiTrace('fetchProductDetail FAILURE', { slug, msg, status: e?.response?.status });
    dispatch({ type: types.FETCH_PRODUCT_DETAIL_FAILURE, payload: msg });
  }
};

export const fetchHomeCatalog = () => async (dispatch) => {
  if (!isApiEnabled()) {
    modamaiTrace('fetchHomeCatalog skipped (API off)', {});
    return;
  }
  modamaiTrace('fetchHomeCatalog START', {});
  dispatch({ type: types.FETCH_HOME_CATALOG_REQUEST });
  try {
    const parseLimit = (raw, fallback) => {
      const n = parseInt(String(raw ?? '').trim(), 10);
      return Number.isFinite(n) && n > 0 ? Math.min(80, n) : fallback;
    };
    const genderLimit = parseLimit(process.env.REACT_APP_HOME_GENDER_SECTION_LIMIT, 12);
    const womenCategory = (process.env.REACT_APP_HOME_CATEGORY_WOMEN_SLUG || 'women').trim() || 'women';
    const menCategory = (process.env.REACT_APP_HOME_CATEGORY_MEN_SLUG || 'men').trim() || 'men';

    const [womenRes, menRes] = await Promise.all([
      productService.getProducts({
        page: 1,
        limit: genderLimit,
        sort: 'newest',
        category: womenCategory,
      }),
      productService.getProducts({
        page: 1,
        limit: genderLimit,
        sort: 'newest',
        category: menCategory,
      }),
    ]);
    modamaiTrace('fetchHomeCatalog batches raw', {
      womenRaw: womenRes?.products?.length,
      menRaw: menRes?.products?.length,
      womenCategory,
      menCategory,
    });
    const railAll = mapProductsListToCatalog(
      Array.isArray(womenRes?.products) ? womenRes.products : [],
    );
    const gridAll = mapProductsListToCatalog(
      Array.isArray(menRes?.products) ? menRes.products : [],
    );
    const rail = railAll.slice(0, genderLimit);
    const grid = gridAll.slice(0, genderLimit);
    dispatch({
      type: types.FETCH_HOME_CATALOG_SUCCESS,
      payload: { rail, grid },
    });
    modamaiTrace('fetchHomeCatalog SUCCESS dispatched', { railLen: rail.length, gridLen: grid.length });
  } catch (e) {
    const err = e?.response?.data?.message || e?.message || 'Failed to load home';
    modamaiTrace('fetchHomeCatalog FAILURE', { err, status: e?.response?.status });
    dispatch({
      type: types.FETCH_HOME_CATALOG_FAILURE,
      payload: err,
    });
  }
};
